import type { MarketingBrandId } from "~/lib/marketing-brand";
import { SITEGLUE_TAGLINE } from "~/lib/siteglue/marketing-chrome";

export const SITEGLUE_TAGLINE_LINE = SITEGLUE_TAGLINE;
export const SITEGLUE_VALUE_PROP = "Convert conversations into opportunities";
export const SITEGLUE_VALUE_PROP_TEAM =
  "Convert conversations into opportunities for your team";
export const SITEGLUE_BRAND_LINE = `SiteGlue — ${SITEGLUE_TAGLINE}`;

/**
 * Rewrite shared Inbound GPT / HITL marketing copy for the SiteGlue twin.
 * Pass through unchanged for inbound_gpt.
 */
export function adaptMarketingCopyForBrand(
  input: string,
  brand: MarketingBrandId,
): string {
  if (brand !== "siteglue") return input;
  return adaptMarketingCopyForSiteGlue(input);
}

export function adaptMarketingCopyForSiteGlue(input: string): string {
  let text = input;

  // Preserve Human-in-the-Loop™ as a SiteGlue brand pillar — never replace it with the tagline.
  text = text
    .replace(/Always a Human-in-the-Loop™/g, "Always a Human-in-the-Loop™")
    .replace(/Always a [Hh]uman[- ]in[- ]the[- ][Ll]oop™?/g, "Always a Human-in-the-Loop™")
    .replace(/Human-in-the-Loop™/g, "Human-in-the-Loop™")
    .replace(/Human-in-the-Loop(?!™)/g, "Human-in-the-Loop™")
    .replace(/human-in-the-loop(?!™)/gi, "Human-in-the-Loop™")
    .replace(/Human in the Loop(?!™)/g, "Human-in-the-Loop™")
    .replace(/human in the loop(?!™)/gi, "Human-in-the-Loop™");

  // Collaboration feature display labels
  text = text
    .replace(/Team Handoff Actions \(MPC\)/g, "Team Handoff Workflows")
    .replace(/Team Handoff Actions \(MCP\)/g, "Team Handoff Workflows")
    .replace(/Team Member Takeover/g, "Team Takeover™")
    .replace(/Human Takeover/g, "Team Takeover™")
    .replace(/Human Handoff/g, "Team Handoff™");

  const replacements: Array<[RegExp, string]> = [
    [/with a human always in the loop/gi, "with Always a Human-in-the-Loop™"],
    [/keeping a human in the loop/gi, "keeping Always a Human-in-the-Loop™"],
    [/keep humans in the loop/gi, "keep Always a Human-in-the-Loop™"],
    [/and keep your team in the loop/gi, "and keep Always a Human-in-the-Loop™"],
    [/Ready to put your\s*<br\s*\/?>\s*team in the loop\?/gi, "Ready to hire your first SiteGlue Agent?"],
    [/Ready to put your team in the loop\?/gi, "Ready to hire your first SiteGlue Agent?"],
    [/put your team in the loop/gi, "hire your first SiteGlue Agent"],
    [/Keeps Your Team in the Loop/g, "Keeps Always a Human-in-the-Loop™"],
    [/keeps your team in the loop/gi, "keeps Always a Human-in-the-Loop™"],
    [/Keep Your Team in the Loop/g, "Keep Always a Human-in-the-Loop™"],
    [/Keep Humans in the Loop/g, "Keep Always a Human-in-the-Loop™"],
    [/Humans in the Loop/g, "AI Built for Humans"],
    [/Spark a conversation with SiteGlue/gi, SITEGLUE_BRAND_LINE],
    [/Spark a conversation/gi, SITEGLUE_TAGLINE],
    [/Conversations that stick/gi, SITEGLUE_TAGLINE],
    [/conversations that stick/gi, SITEGLUE_TAGLINE.toLowerCase()],
    [/Inbound GPT Agent/g, "SiteGlue Agent"],
    [/Inbound GPT agents/g, "SiteGlue agents"],
    [/Inbound GPT agent/g, "SiteGlue agent"],
    [/Inbound GPT API/g, "SiteGlue API"],
    [/Inbound GPT/g, "SiteGlue"],
    [/Easy GPT Builders\s*\(\s*["']SiteGlue["']/g, 'SiteGlue ("SiteGlue"'],
    [/Easy GPT Builders/g, "SiteGlue"],
    [/www\.easygpt\.builders/g, "www.siteglue.ai"],
    [/app\.easygpt\.builders/g, "app.easygpt.builders"],
    [/support@easygpt\.builders/g, "support@siteglue.ai"],
    [/Your team, always in the loop/gi, "Always a Human-in-the-Loop™"],
    [/your team, always in the loop/gi, "Always a Human-in-the-Loop™"],
    [/Team always in the loop/gi, "Always a Human-in-the-Loop™"],
    [/team always in the loop/gi, "Always a Human-in-the-Loop™"],
    [/\|\s*Inbound GPT(?:\s+Blog)?\s*$/gim, "| SiteGlue"],
    [/—\s*Inbound GPT\s*$/gim, "— SiteGlue"],
  ];

  for (const [pattern, replacement] of replacements) {
    text = text.replace(pattern, replacement);
  }

  // Auth stays on app.easygpt.builders — restore after domain rewrite if we over-replaced.
  // (We intentionally keep app.easygpt.builders above.)

  text = text.replace(
    /^## What "(?:Spark a conversation with SiteGlue|Convert conversations into opportunities|Human-in-the-Loop™)" actually means$/gim,
    '## What Human-in-the-Loop™ actually means',
  );

  return text;
}

export function adaptMarketingMetaTitleForBrand(
  title: string,
  brand: MarketingBrandId,
): string {
  if (brand !== "siteglue") return title;
  return adaptMarketingCopyForSiteGlue(title)
    .replace(/\s*\|\s*SiteGlue AI\s*$/i, " | SiteGlue")
    .replace(/\s*—\s*SiteGlue AI\s*$/i, " — SiteGlue");
}

export const SITEGLUE_BLOG_INDEX = {
  brandLine: SITEGLUE_BRAND_LINE,
  subtitle: `AI Built for Humans. Always a Human-in-the-Loop™. ${SITEGLUE_TAGLINE_LINE}`,
} as const;
